import classNames from 'classnames';
import React, { useContext, useMemo, useState } from 'react';
import { connect } from 'umi';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { getName } from '@home/utils/common';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { Bubble } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';

const SumNode = ({
  name,
  content,
  isEN,
}: {
  name: string;
  content: string;
  isEN: boolean;
}) => {
  const [isIconHover, setIsIconHover] = useState(false);
  return (
    <div className="dbd-funnel-table-service-sum">
      {name}
      <Bubble
        dark
        placement="top-start"
        arrowPointAtCenter
        style={{ maxWidth: isEN ? '235px' : '253px' }}
        content={content}
      >
        <div
          style={{ marginLeft: 6 }}
          onMouseOver={() => {
            setIsIconHover(true);
          }}
          onMouseOut={() => {
            setIsIconHover(false);
          }}
        >
          <SvgIcon
            className="port-icon"
            type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'}
          />
        </div>
      </Bubble>
    </div>
  );
};

const FunnelTableService = (props: any) => {
  const {
    className,
    style,
    gameCode,
    explainInfo,
    gameConfig,
    disabledDimList,
    children,
  } = props;

  const contextObj = useContext(FilterContext);
  const { isEN, formatMessage } = useLocal();

  const { filterVal, filterLoading } = contextObj || {};
  const {
    stageList = [],
    defaultStage = [],
    defaultIconStage = [],
    ...filterValue
  } = filterVal || {};

  // 默认选项
  const defaultSelect = gameConfig?.page_selector?.find(
    (item: any) => item?.path === '/funnel/registerFunnelDaily',
  )?.filters;

  const hasAddInput = useMemo(
    () =>
      defaultSelect
        ?.filter((item) => item?.visible === 1)
        ?.map((v) => v?.key)
        ?.includes('store_channel'),
    [gameConfig],
  );

  const getVisbile = (key: string) =>
    defaultSelect?.find((item: any) => item?.key === key)?.visible;

  // 根据查询参数，获取对应的维度选项,todo：需要跟筛选器顺序保持一致？
  const dimOptions = useMemo(() => {
    const getOptions = (filterValue: any) => {
      const options = [];
      // 游戏阶段-Game Stage
      if (filterValue?.game_stage && getVisbile('game_stage')) {
        options.push({
          value: 'game_stage',
          groupKey: 'separated',
          text: formatMessage({ id: 'funnel_by_game_stage' }),
        });
      }
      // 角色-Class
      if (filterValue?.character_class && getVisbile('character_class')) {
        options.push({
          value: 'character_class',
          groupKey: 'separated',
          text: formatMessage({ id: 'funnel_by_character_class' }),
        });
      }
      // 用户标签-User Type
      if (filterValue?.user_type && getVisbile('user_type')) {
        options.push({
          value: 'user_type',
          groupKey: 'separated',
          text: formatMessage({ id: 'funnel_by_user_type' }),
        });
      }
      if (filterValue?.region && getVisbile('region')) {
        options.push({
          value: 'region',
          groupKey: 'separated',
          text: formatMessage({ id: 'common_by_Region' }),
        });
        options.push({
          value: 'country',
          groupKey: 'separated',
          text: formatMessage({ id: 'common_by_Country' }),
        });
      }
      if (filterValue?.system && getVisbile('system')) {
        options.push({
          value: 'system',
          groupKey: 'separated',
          text: formatMessage({ id: 'common_by_System' }),
        });
      }
      if (filterValue?.app_version && getVisbile('app_version')) {
        options.push({
          value: 'app_version',
          groupKey: 'separated',
          text: formatMessage({ id: 'funnel_by_version' }),
        });
      }
      // 以下为历史保留
      if (filterValue?.zone && getVisbile('zone')) {
        options.push({
          value: 'zone',
          groupKey: 'separated',
          text: formatMessage({ id: 'common_by_Zone' }),
        });
      }
      if (filterValue?.channel && getVisbile('channel')) {
        options.push({
          value: 'channel',
          groupKey: 'separated',
          text: formatMessage({ id: 'common_by_Channel' }),
        });
      }
      if (filterValue?.lang && getVisbile('lang')) {
        options.push({
          value: 'language',
          groupKey: 'separated',
          text: formatMessage({ id: 'common_by_Language' }),
        });
      }
      if (filterValue?.networkType && getVisbile('network')) {
        options.push({
          value: 'network_type',
          groupKey: 'separated',
          text: formatMessage({ id: 'funnel_by_network_type' }),
        });
      }
      if (filterValue?.memory && getVisbile('memory')) {
        options.push({
          value: 'memory',
          groupKey: 'separated',
          text: formatMessage({ id: 'funnel_by_memory' }),
        });
      }
      if (filterValue?.storeChannel && getVisbile('store_channel')) {
        options.push({
          value: 'store_channel',
          groupKey: 'separated',
          text: formatMessage({ id: 'funnel_by_store_channel' }),
        });
      }

      let hasDisabled = false;
      const newOptions = options.map((item) => {
        if (disabledDimList?.includes(item.value)) {
          hasDisabled = true;
          return { ...item, disabled: true };
        }
        return item;
      });
      // 如果没有配置或找不到匹配的禁用筛选器，则默认禁选第一个
      if (!hasDisabled && newOptions[0]) {
        newOptions[0].disabled = true;
      }
      return newOptions;
    };
    return getOptions(filterValue);
  }, [filterValue, isEN, disabledDimList, defaultSelect]);

  // 下载时，禁止取消勾选第一个步骤
  const firstStepId = useMemo(
    () => stageList?.find((item: any) => item?.sn === '1')?.code || 'G1_1',
    [stageList],
  );

  // 下载选项
  const stepOptions = useMemo(
    () =>
      stageList?.map((item) => {
        const { code, name_cn, name_en } = item || {};
        return {
          value: code,
          text: getName(isEN, name_en, name_cn),
          disabled: code === firstStepId,
        };
      }),
    [stageList, isEN],
  );

  // 自定义维度名称
  const customColumnFn = (value: string) => {
    const nameObj = {
      game_stage: formatMessage({ id: 'funnel_game_stage' }),
      character_class: formatMessage({ id: 'funnel_character_class' }),
      user_type: formatMessage({ id: 'funnel_user_type' }),
      app_version: formatMessage({ id: 'funnel_version' }),
    };
    return {
      key: value,
      header: nameObj[value] || formatMessage({ id: `common_${value}` }),
      copyKey: `${value}_name`,
      align: 'left',
      width: 200,
      fixed: dimOptions[0]?.value === value ? "left" : undefined,
      render(record: any) {
        const name = `${value}_name`;
        // 总值
        if (['Sum', '汇总值'].includes(record?.[name])) {
          return (
            <SumNode
              name={record?.[name]}
              content={formatMessage({ id: 'overview_sum_tip' })}
              isEN={isEN}
            />
          );
        }
        return record?.[name] || '-';
      },
    };
  };

  return (
    <div
      className={classNames(className, 'dbd-funnel-table-service')}
      style={style}
    >
      {dimOptions?.length > 0 && React.Children.map(children, (child: any) =>
        React.cloneElement(child, {
          firstStepId,
          hasAddInput,
          filterValue: {
            stageList,
            defaultStage,
            defaultIconStage,
            filterValue,
          },
          filterLoading,
          gameCode,
          explainInfo,
          dimOptions,
          stepOptions,
          dimensionDefaultValue: dimOptions[0]?.value || 'game_stage',
          fisrtRowName: dimOptions[0]?.value ? `${dimOptions[0]?.value}_name` : 'game_stage_name',
          defaultSort: [{ by: dimOptions[0]?.value || 'game_stage', order: '' }],
          customColumnFn,
        }),
      )}
    </div>
  );
};

export default connect((state: any) => ({
  gameConfig: state.dashboardSetting.gameConfig,
  serviceData: state.overview?.data,
  gameCode: state.dashboard?.currentGame?.game_code,
  explainInfo: state.dashboard?.explainInfo,
}))(FunnelTableService);
